import { getCsrfToken } from "./util";
import { debounce } from "lodash";

// Caller convenience: each helper accepts `headers` as either
//   - an array of `{ key, value }` pairs (legacy shape), or
//   - a plain `{ key: value }` object.
// We normalise to a plain object so spread into the fetch headers config
// produces real HTTP headers (the legacy array-spread silently produced
// numeric keys "0"/"1"/... that fetch ignored — see
// `LessonRecording#handleTimeUpdate` which depends on `timestamp` /
// `duration` / `starttime` / `endtime` headers reaching the server).
function normalizeHeaders(headers) {
  if (!headers) return {};
  if (Array.isArray(headers)) {
    const out = {};
    for (const h of headers) {
      if (h && h.key) out[h.key] = h.value;
    }
    return out;
  }
  return headers;
}

export const apiGet = async (url, { headers } = {}) => {
  const response = await fetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      "Accept": "application/json",
      ...normalizeHeaders(headers),
    },
  });

  const respText = await response.text();
  const parsedResponse = respText && JSON.parse(respText);
  return {
    parsedResponse,
    statusCode: response.status,
    statusText: response.statusText,
  };
};

export const apiPost = async (url, { headers, body } = {}) => {
  const response = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "Accept": "application/json",
      "X-CSRF-Token": getCsrfToken(),
      ...normalizeHeaders(headers),
    },
    ...(body && { body: JSON.stringify(body) }),
  });

  const respText = await response.text();
  const parsedResponse = respText && JSON.parse(respText);
  return {
    parsedResponse,
    statusCode: response.status,
    statusText: response.statusText,
  };
};

export const apiDelete = async (url, { headers } = {}) => {
  const response = await fetch(url, {
    method: "DELETE",
    headers: {
      "Content-Type": "application/json",
      "Accept": "application/json",
      "X-CSRF-Token": getCsrfToken(),
      ...normalizeHeaders(headers),
    },
  });

  const respText = await response.text();
  const parsedResponse = respText && JSON.parse(respText);
  return {
    parsedResponse,
    statusCode: response.status,
    statusText: response.statusText,
  };
};

export const apiPut = async (url, { headers, body } = {}) => {
  const response = await fetch(url, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      "Accept": "application/json",
      "X-CSRF-Token": getCsrfToken(),
      ...normalizeHeaders(headers),
    },
    ...(body && { body: JSON.stringify(body) }),
  });

  const respText = await response.text();
  const parsedResponse = respText && JSON.parse(respText);
  return {
    parsedResponse,
    statusCode: response.status,
    statusText: response.statusText,
  };
};

export const apiPatch = async (url, { headers, body } = {}) => {
  const response = await fetch(url, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      "Accept": "application/json",
      "X-CSRF-Token": getCsrfToken(),
      ...normalizeHeaders(headers),
    },
    ...(body && { body: JSON.stringify(body) }),
  });

  const respText = await response.text();
  const parsedResponse = respText && JSON.parse(respText);
  return {
    parsedResponse,
    statusCode: response.status,
    statusText: response.statusText,
  };
};

export const debouncedApiGet = debounce(async (url) => {
  const response = await fetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      "Accept": "application/json",
    },
  });

  const respText = await response.text();
  return JSON.parse(respText);
});
